import type { TEntityId } from '~/types/models'

/**
 * Canonical initial state for the entityDialog Vuex module.
 *
 * Extracted so `store/entityDialog.js` `state()` has a single source of truth
 * that carries over verbatim to the Pinia store after the Nuxt 3 migration
 * (where `$reset()` restores exactly this object).
 *
 * ⚠️ Migration note — state-vs-reset drift: the current `clearEntityDialog`
 * action resets `inputFieldsEntityNameOverride` to `null`
 * (`clearOverrideEntityNameForInputFields`), but its initial default here is
 * `undefined`. `$reset()` will restore `undefined`, so reconcile this one field
 * deliberately when the clear-chain is replaced by `$reset()`.
 *
 * ⚠️ Migration note — partial reset: `clearEntityDialog` is an entity-level
 * reset; the subentity-only fields (`subentityName`,
 * `subentityNameTitleOverride`, `subentityInputFieldsEntityNameOverride`,
 * `subentityReturnValue`, `subentityAfterRequestProcessor`,
 * `valuesForSubentityFieldsWhoseValuesToSetFromEntityFields`) are cleared by
 * `clearSubentityDialog`, not `clearEntityDialog`. A blanket `$reset()` would
 * clear both at once — verify the two dialogs are meant to reset independently
 * before collapsing them.
 */
export const entityDialogDefaults = {
  showEntityDialog: false,
  entityNameTitlePrefixOverride: null,
  entityNameTitleOverride: null,
  subtitle: null,
  message: null,
  saveButtonCaptionVerbOverride: null,
  deleteButtonCaptionsVerbOverride: null,
  desiredAction: null,
  preRequestProcessor: undefined,
  afterRequestCallback: undefined,
  storeEditedInputDataAsJsonInProperty: null,
  entityName: null,
  inputFieldsEntityNameOverride: undefined,
  inputFieldsOverride: undefined,
  entityIdOverride: undefined,
  entityData: undefined,
  entityParentObjectForCreate: undefined,
  allowAddAnother: false,
  startInViewMode: false,
  disableEditButton: false,
  suppressFocusOnFirstField: false,
  doSubdueMessage: false,
  doAlignMessageWithTitle: false,
  showSubentityDialog: false,
  subentityName: null,
  subentityNameTitleOverride: null,
  subentityInputFieldsEntityNameOverride: undefined,
  subentityReturnValue: null,
  subentityAfterRequestProcessor: undefined,
  subentityAfterRequestCallbacks: undefined,
  subentityParentObjectForCreate: undefined,
  valuesForSubentityFieldsWhoseValuesToSetFromEntityFields: null,
}

/**
 * Derive the effective entity id shown by the dialog: an explicit override
 * wins, else the id of the loaded entity, else null. This is the only
 * entityDialog getter with real derivation logic (the rest are pass-throughs),
 * so it is the piece worth pinning with tests and reusing unchanged as a Pinia
 * getter.
 */
export function deriveEntityDialogEntityId(state: {
  entityIdOverride?: TEntityId | null
  entityData?: { id?: TEntityId | null } | null
}): TEntityId | null {
  return state.entityIdOverride
    ? state.entityIdOverride
    : state.entityData?.id
    ? state.entityData.id
    : null
}
